<template>
    <section class="help">
        <header class="help__header">
            <h1>Как работать в 2digital DOOH</h1>
            <p>
                Платформа для запуска рекламы на цифровых экранах (DOOH).
                Сейчас основной режим — <strong>RTB</strong>: вы загружаете креативы, собираете кампанию,
                задаёте экраны и лимиты, затем смотрите статистику.
            </p>
        </header>

        <section class="help__section help__section--intro">
            <h2 class="help__section-title">Где что находится</h2>
            <p class="help__section-desc">
                Логотип вверху ведёт на список <strong>кампаний RTB</strong>.
                В меню <strong>RTB</strong> — кампании (в том числе пакетные), креативы;
                <template v-if="isAdmin"> у администратора также журнал <a href="/rtb/requests">Запросы</a>.</template>
                Статистика смотрится в карточке кампании или в пакетной статистике, а не отдельным пунктом верхнего меню.
                Настройка структуры (рекламодатели, бренды, пользователи) — в
                <a href="/administration">Администрировании</a> (иконка профиля → Администрирование).
            </p>
        </section>

        <section class="help__section">
            <h2 class="help__section-title">Быстрый старт</h2>
            <p class="help__section-desc">
                Пять шагов от подготовки до запуска показов.
            </p>
            <ol class="help__steps help__steps--compact">
                <li class="step">
                    <div class="step__num">1</div>
                    <div class="step__body">
                        <h3 class="step__title">Проверьте рекламодателя и бренд</h3>
                        <div class="step__content">
                            <p>
                                В кампании нужно выбрать рекламодателя и бренд. Если их ещё нет — попросите через
                                <a href="mailto:sales@2digital.tech">sales@2digital.tech</a>
                                или настройте в
                                <a href="/administration">Администрировании</a> (вкладка «Структура»).
                            </p>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">2</div>
                    <div class="step__body">
                        <h3 class="step__title">Загрузите креатив</h3>
                        <div class="step__content">
                            <p>Две версии ролика: русская (RU) и казахская (KZ). Размер и длительность подставятся из файла.</p>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">3</div>
                    <div class="step__body">
                        <h3 class="step__title">Дождитесь модерации</h3>
                        <div class="step__content">
                            <p>Креатив проверяют перед показами. Без одобрения его нельзя добавить в кампанию.</p>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">4</div>
                    <div class="step__body">
                        <h3 class="step__title">Соберите кампанию RTB</h3>
                        <div class="step__content">
                            <p>Экраны, даты, расписание, лимиты бюджета или показов, креативы и ставки.</p>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">5</div>
                    <div class="step__body">
                        <h3 class="step__title">Включите кампанию и смотрите статистику</h3>
                        <div class="step__content">
                            <p>В списке кампаний переведите статус в «активна». Отчёты — в статистике кампании.</p>
                        </div>
                    </div>
                </li>
            </ol>
        </section>

        <section v-if="isAdmin" class="help__section help__section--role help__section--admin">
            <h2 class="help__section-title">Для администратора</h2>
            <p class="help__section-desc">
                Полный доступ к настройке платформы и модерации.
            </p>
            <ul class="help__role-list">
                <li>
                    В <a href="/administration">Администрировании</a> вы управляете всей структурой:
                    <strong>Партнёр → Рекламодатель → Бренд</strong>, создаёте и редактируете сущности,
                    назначаете доступ пользователям. Там же — наценки: у партнёра (L1 и L2) и переопределения
                    для отдельных RTB-кампаний.
                </li>
                <li>
                    Вкладки <strong>«Пользователи»</strong> и <strong>«Пакеты экранов»</strong> — учётные записи
                    и готовые наборы экранов для пакетных RTB-кампаний.
                </li>
                <li>
                    В меню профиля — <strong><a href="/moderation">Модерация</a></strong>: проверка креативов,
                    кампаний и объявлений перед запуском показов.
                </li>
                <li>
                    В списке кампаний RTB бюджет показан <strong>в трёх суммах</strong>: админ, агентство и клиент
                    (у других ролей видна только своя часть).
                </li>
                <li>
                    RTB-гайд для разработчиков и поддержки —
                    <strong><a href="/help/rtb">Функционирование RTB</a></strong> (в меню профиля).
                </li>
            </ul>
        </section>

        <section v-else-if="isAgency" class="help__section help__section--role help__section--agency">
            <h2 class="help__section-title">Для агентства</h2>
            <p class="help__section-desc">
                Дополнительные возможности при работе от имени партнёра (агентства).
            </p>
            <ul class="help__role-list">
                <li>
                    В <a href="/administration">Администрировании</a> на вкладке «Структура» видна полная цепочка
                    <strong>Партнёр → Рекламодатель → Бренд</strong>. Рекламодателей и бренды агентство
                    создаёт самостоятельно.
                </li>
                <li>
                    Наценка для клиентов (ваши цены для User PRO / User Standard) задаётся на вкладке «Структура»:
                    у партнёра — для всех кампаний, у RTB-кампании — индивидуально.
                </li>
                <li>
                    В списке кампаний RTB бюджет показан <strong>в двух суммах</strong>: для агентства и для клиента
                    (клиент видит только свою).
                </li>
                <li>
                    Кнопка <strong>«Добавить пользователя»</strong> — создание клиентских аккаунтов (User PRO или User Standard).
                </li>
                <li>
                    Подробный гайд —
                    <strong><a href="/help/agency">Работа с платформой для агентств</a></strong>
                    (в меню профиля).
                </li>
            </ul>
        </section>

        <section v-else-if="isClient" class="help__section help__section--role help__section--client">
            <h2 class="help__section-title">Для клиента</h2>
            <p class="help__section-desc">
                Если вы заходите как User PRO или User Standard.
            </p>
            <ul class="help__role-list">
                <li>
                    В <a href="/administration">Администрировании</a> на вкладке «Структура» видны только
                    <strong>ваши рекламодатели и бренды</strong> (без уровня партнёра).
                </li>
                <li>Цены в интерфейсе уже с учётом наценок агентства.</li>
                <li>
                    В списке кампаний отображается <strong>один бюджет</strong>: ваш лимит и сколько уже потрачено.
                </li>
                <li>
                    Креативы и кампании RTB вы создаёте сами по тому же сценарию, что описан ниже.
                </li>
            </ul>
        </section>

        <section class="help__section">
            <h2 class="help__section-title">Шаг 1. Креатив</h2>
            <p class="help__section-desc">
                Рекламный ролик или баннер для показа на экранах.
            </p>
            <ol class="help__steps">
                <li class="step">
                    <div class="step__num">1</div>
                    <div class="step__body">
                        <h3 class="step__title">Создайте RTB-креатив</h3>
                        <div class="step__content">
                            <ul>
                                <li><b>Файлы</b>: отдельно русская и казахская версии (картинка или видео)</li>
                                <li><b>Название</b>: подставляется из имени файла, можно изменить</li>
                                <li><b>Бренд</b>: по желанию, для удобства в списке</li>
                                <li><b>Размер и длительность</b>: определяются автоматически из загруженного файла</li>
                            </ul>
                            <div class="step__actions">
                                <a href="/rtb/creatives/create" class="btn btn--primary">Создать креатив</a>
                                <a href="/rtb/creatives" class="btn">Список креативов</a>
                            </div>
                            <div class="note">
                                После сохранения креатив уходит на модерацию. Обычно проверка занимает до суток.
                                В списке креативов смотрите статус: пока не «одобрен» — в кампанию не добавится.
                            </div>
                            <div class="tip">
                                Технические требования к файлам (форматы, размеры) — на странице
                                <a href="/requirements">Технические требования</a>.
                            </div>
                        </div>
                    </div>
                </li>
            </ol>
        </section>

        <section class="help__section">
            <h2 class="help__section-title">Шаг 2. Кампания RTB</h2>
            <p class="help__section-desc">
                Кампания объединяет креативы, экраны, расписание и лимиты. Показы идут, пока не исчерпан бюджет
                или лимит показов.
            </p>
            <ol class="help__steps">
                <li class="step">
                    <div class="step__num">1</div>
                    <div class="step__body">
                        <h3 class="step__title">Основные настройки</h3>
                        <div class="step__content">
                            <ul>
                                <li><b>Название</b>, <b>рекламодатель</b> и <b>бренд</b></li>
                                <li><b>Даты</b> начала и окончания</li>
                                <li><b>Расписание</b>: в какие дни и часы кампания активна</li>
                                <li>
                                    <b>Лимиты</b>: укажите хотя бы один — общий бюджет, общее число показов,
                                    дневной бюджет или дневные показы. Можно включить «Распределить равномерно»
                                    по дням кампании.
                                </li>
                                <li>
                                    <b>Аудиторные данные</b> (по желанию): галка на форме создания.
                                    +10% к счёту, ставку не поднимает. Снять галку нельзя.
                                    Контакты у экрана (OTS) — уникальные контакты за 15-минутные интервалы у выбранных экранов.
                                </li>
                            </ul>
                            <div class="step__actions">
                                <a href="/rtb/campaigns/create" class="btn btn--primary">Создать кампанию</a>
                                <a href="/rtb/campaigns" class="btn">Список кампаний</a>
                            </div>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">2</div>
                    <div class="step__body">
                        <h3 class="step__title">Креативы и ставки</h3>
                        <div class="step__content">
                            <p>Отметьте одобренные RTB-креативы и для каждого укажите <b>Max Bid</b> (₸) —
                                максимальную цену за показ, которую вы готовы платить.</p>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">3</div>
                    <div class="step__body">
                        <h3 class="step__title">Экраны</h3>
                        <div class="step__content">
                            <p>
                                На карте выберите экраны вручную или подставьте <b>пакет экранов</b>, если он вам
                                назначен. Показы возможны только на выбранных площадках.
                            </p>
                        </div>
                    </div>
                </li>
                <li class="step">
                    <div class="step__num">4</div>
                    <div class="step__body">
                        <h3 class="step__title">Настройки по паре «креатив + экран»</h3>
                        <div class="step__content">
                            <p>Для каждой связки можно задать:</p>
                            <ul>
                                <li><b>Ставку</b>: «Авто» (система подбирает в пределах Max Bid) или «Ручная»</li>
                                <li>Отдельные лимиты бюджета и показов (если пусто — действуют лимиты кампании)</li>
                                <li>Расписание показов для этой пары (по желанию)</li>
                            </ul>
                            <div class="tip">
                                После сохранения в списке кампаний включите кампанию (кнопка play / статус «активна»).
                                На паузе показы не идут.
                            </div>
                        </div>
                    </div>
                </li>
            </ol>
        </section>

        <section class="help__section">
            <h2 class="help__section-title">Шаг 3. Статистика и журнал</h2>
            <p class="help__section-desc">
                Следите за расходом бюджета, показами и исходами аукционов.
            </p>
            <ul class="help__plain-list">
                <li>
                    <strong>Статистика кампании</strong> — в списке кампаний откройте отчёт по нужной строке
                    (бюджет и показы).
                </li>
                <li v-if="isAdmin">
                    <strong>Журнал запросов</strong> — меню RTB →
                    <a href="/rtb/requests">Запросы</a> (аукционные запросы и исходы).
                </li>
            </ul>
            <div v-if="isAdmin" class="step__actions">
                <a href="/rtb/requests" class="btn btn--primary">Открыть журнал запросов</a>
            </div>
        </section>

        <section class="help__section">
            <h2 class="help__section-title">Навигация по платформе</h2>
            <div class="help__navigation">
                <div class="nav-item">
                    <h4>Главная</h4>
                    <p>Список кампаний RTB</p>
                    <a href="/rtb/campaigns" class="btn btn--small">К кампаниям</a>
                </div>
                <div class="nav-item">
                    <h4>RTB</h4>
                    <p>{{ isAdmin ? 'Кампании, креативы и журнал запросов' : 'Кампании и креативы' }}</p>
                    <ul>
                        <li><a href="/rtb/campaigns">Кампании</a></li>
                        <li><a href="/rtb/creatives">Креативы</a></li>
                        <li v-if="isAdmin"><a href="/rtb/requests">Запросы</a></li>
                    </ul>
                </div>
                <div class="nav-item">
                    <h4>Полезное</h4>
                    <p>Справочные разделы</p>
                    <ul>
                        <li><a href="/administration">Администрирование</a></li>
                        <li><a href="/requirements">Технические требования</a></li>
                    </ul>
                </div>
            </div>
        </section>

        <section class="help__faq">
            <h2>Частые вопросы</h2>
            <details>
                <summary>Креатив не проходит модерацию</summary>
                <p>
                    Проверьте формат и размер файла, соответствие
                    <a href="/requirements">техническим требованиям</a> и комментарий модератора в карточке креатива.
                </p>
            </details>
            <details>
                <summary>Не могу добавить креатив в кампанию</summary>
                <p>
                    В кампанию попадают только креативы со статусом «одобрен». Дождитесь окончания модерации
                    или исправьте замечания и отправьте снова.
                </p>
            </details>
            <details>
                <summary>Кампания не показывает рекламу</summary>
                <p>
                    Проверьте: кампания <strong>активна</strong>; даты и расписание включают текущее время;
                    не исчерпаны лимиты бюджета или показов; выбраны экраны; креативы одобрены и привязаны к кампании;
                    для ручной ставки указана цена не выше Max Bid по креативу.
                </p>
            </details>
            <details>
                <summary>Что такое Max Bid и ручная ставка?</summary>
                <p>
                    <strong>Max Bid</strong> — потолок цены за показ для креатива. В режиме «Авто» система участвует
                    в аукционе в пределах этой суммы. «Ручная» ставка задаёт фиксированную цену для пары
                    креатив + экран; она не должна превышать Max Bid.
                </p>
            </details>
            <details>
                <summary>Неверное время показов</summary>
                <p>
                    Задайте даты кампании и сетку расписания (дни и часы). Для отдельных экранов можно уточнить
                    расписание в настройках пары «креатив + экран».
                </p>
            </details>
        </section>

        <HelpGuaranteed v-if="showGuaranteedHelp" />
    </section>
</template>

<script>
import HelpGuaranteed from './HelpGuaranteed.vue';

/** Отдельный флаг только для страницы справки (не связан с MenuTabs). */
const showGuaranteedHelp = false;

export default {
    name: 'Help',
    components: {
        HelpGuaranteed,
    },
    props: {
        userData: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            showGuaranteedHelp,
        };
    },
    computed: {
        isAdmin() {
            return Boolean(this.userData?.is_admin);
        },
        isAgency() {
            return Boolean(this.userData?.is_agency);
        },
        isClient() {
            return Boolean(this.userData?.is_user_pro || this.userData?.is_user_standard);
        },
    },
};
</script>

<style scoped>
/* Стили вынесены в resources/scss/pages/_help.scss и собираются через Mix */
</style>
